import { useState, useEffect } from 'react';
import { Usd } from '@/utils/CurrencyContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { CheckCircle, Download, Package, Loader2, ArrowLeft, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const paypalOrderId = searchParams.get('token');
    if (paypalOrderId) {
      capturePayment(paypalOrderId);
    } else {
      setError('Invalid payment session');
      setIsProcessing(false);
    }
  }, [searchParams]);

  const capturePayment = async (paypalOrderId) => {
    try {
      const response = await api.post(`/marketplace/checkout/capture/${paypalOrderId}`);
      setOrderResult(response.data);
      toast.success('Payment successful!');
    } catch (error) {
      console.error('Payment capture error:', error);
      setError(error.response?.data?.detail || 'Payment processing failed');
      toast.error('Payment processing failed');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
          <Card className="text-center py-16" style={{ borderColor: '#E3E1DB' }}>
            <CardContent>
              <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" style={{ color: '#163329' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Processing Payment...
              </h2>
              <p style={{ color: '#5C635A' }}>Please wait while we confirm your payment.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
          <Card className="text-center py-16" style={{ borderColor: '#E3E1DB' }}>
            <CardContent>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-3xl">!</span>
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Payment Failed
              </h2>
              <p className="mb-6" style={{ color: '#5C635A' }}>{error}</p>
              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => navigate('/marketplace/cart')}
                  className="rounded-full"
                  style={{ borderColor: '#163329', color: '#163329' }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Cart
                </Button>
                <Button
                  onClick={() => navigate('/marketplace')}
                  className="rounded-full"
                  style={{ backgroundColor: '#163329' }}
                >
                  Continue Shopping
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="text-center py-12" style={{ borderColor: '#E3E1DB' }}>
            <CardContent>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F5E9' }}>
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
              </motion.div>

              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Thank You!
              </h1>
              <p className="text-lg mb-6" style={{ color: '#5C635A' }}>
                Your order has been confirmed.
              </p>

              {orderResult && (
                <div className="text-left space-y-4 mb-8 p-6 rounded-lg" style={{ backgroundColor: '#F5F5F0' }}>
                  <div className="flex justify-between items-center border-b pb-3" style={{ borderColor: '#E3E1DB' }}>
                    <span style={{ color: '#5C635A' }}>Order ID</span>
                    <span className="font-mono font-medium" style={{ color: '#163329' }}>
                      #{orderResult.order_id?.slice(0, 8)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span style={{ color: '#5C635A' }}>Total Paid</span>
                    <span className="text-xl font-bold" style={{ color: '#163329' }}>
                      <Usd value={orderResult.total} />
                    </span>
                  </div>
                </div>
              )}

              {/* Digital Downloads */}
              {orderResult?.digital_downloads && orderResult.digital_downloads.length > 0 && (
                <div className="text-left mb-8">
                  <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#163329' }}>
                    <Download className="h-5 w-5" />
                    Your Digital Downloads
                  </h3>
                  <div className="space-y-3">
                    {orderResult.digital_downloads.map((download, idx) => (
                      <Card key={idx} className="p-4" style={{ borderColor: '#E3E1DB' }}>
                        <div className="flex items-center justify-between">
                          <span style={{ color: '#163329' }}>{download.title}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(download.download_url, '_blank')}
                            className="rounded-full"
                            style={{ borderColor: '#163329', color: '#163329' }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Physical Products Notice */}
              {orderResult?.has_physical && (
                <div className="text-left mb-8 p-4 rounded-lg" style={{ backgroundColor: '#FFF8E1' }}>
                  <div className="flex items-start gap-3">
                    <Package className="h-5 w-5 mt-0.5" style={{ color: '#E2A761' }} />
                    <div>
                      <h4 className="font-medium" style={{ color: '#163329' }}>Physical Products</h4>
                      <p className="text-sm" style={{ color: '#5C635A' }}>
                        Your physical items will be shipped to your provided address. 
                        You'll receive tracking information via email once shipped.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => navigate('/marketplace/orders')}
                  className="rounded-full"
                  style={{ borderColor: '#163329', color: '#163329' }}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  View Orders
                </Button>
                <Button
                  onClick={() => navigate('/marketplace')}
                  className="rounded-full"
                  style={{ backgroundColor: '#163329' }}
                >
                  Continue Shopping
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
